import {
  type TAccountType,
  type TBillingInfo,
  type TProductInfo,
  type TPublicConfig,
  type TStorageAccountType,
  type TStorageStripeAccountStatus,
  type TStorageUser,
  type TStripeAccountStatus,
  type TStripeUpgradeResult,
  type TSubscriptionInfo,
  type TUser,
  type TUserDescription,
  type TUserExtraSetting,
  type TUserExtraSettingLabels,
  type TUserExtraSettings,
} from '@/types/user'
import TimerUtils from '@/helpers/timerUtils'
import { IntercomDes } from '@/helpers/intercomDes'
import { MixpanelDes } from '@/helpers/mixpanelDes'
import { Validators } from '@/helpers/validators'
import { defineStore } from 'pinia'
import { http, isAbortError } from '@/helpers/http'
import { Products } from '@/helpers/product'
import { mobileCheck } from '@/helpers/mobileCheck'
import { isProduction } from '@/helpers/envCheck'
import { canCreatePresentations as canCreatePresentationsFor } from '@/helpers/presentationAccess'
import { ref } from 'vue'

import { useLoaderStore } from '@/stores/loader'
import { usePopupStore } from '@/stores/popup'
import { useSnackbarStore } from '@/stores/snackbar'
import { useFlipbooksStore } from '@/stores/flipbooks'
import { useStorage } from '@vueuse/core'
import type { TClient } from '@/types/agency'
import BugSnagDes from '@/helpers/bugSnagDes'
import router from '@/router'
import { getDeviceId, getFingerprint } from '@/helpers/deviceFingerprint'
import { clear as clearIDB } from 'idb-keyval'

function isNumeric(n: any) {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

function getUser(): TUser {
  return useStorage('user', {} as TStorageUser).value.value
}

async function performLogout() {
  await http.post('/users/logout', {}, true)
}

function logout() {
  const storage = useStorage('user', {} as TStorageUser)
  storage.value = null
  const accountTypeStorage = useStorage('accountType', {} as TStorageAccountType)
  accountTypeStorage.value = null
  const stripeAccountStatusStorage = useStorage('stripeAccountStatus', {} as TStorageStripeAccountStatus)
  stripeAccountStatusStorage.value = null
  clearIDB().catch(() => {})
  if (!window.location.href.includes('/dashboard/login')) {
    window.location.href = '/dashboard/login'
  }
}

function getAccountType(): TAccountType {
  return useStorage('accountType', {} as TStorageAccountType).value.value
}

export const useUserStore = defineStore('user', () => {
  const user = ref<TUser>(getUser())
  const companyUsers = ref<TUser[]>()
  const accountType = ref<TAccountType>(getAccountType())
  const description = ref<TUserDescription>({ description: '', account_slug: '' })
  const extraSettings = ref<TUserExtraSettings[] | undefined>()
  const publicConfig = ref<TPublicConfig>()
  const flipbooks = useFlipbooksStore()
  const snack = useSnackbarStore()
  const billingHistoryCache = ref<TBillingInfo | null>(null)
  let billingHistoryPromise: Promise<TBillingInfo> | null = null
  const billingHistoryFromLogsCache = ref<TBillingInfo | null>(null)
  let billingHistoryFromLogsPromise: Promise<TBillingInfo> | null = null

  const userSettingLabels: TUserExtraSettingLabels = {
    FLIPBOOK_LABEL: 1,
    FLIPBOOK_HIDE_SOCIAL_SHARE: 3,
    PAGE_SIZE: 5,
    OPEN_AI_TERMS_ACCEPTED: 10,
    ONBOARDING_COMPLETED: 11,
    NEW_DASH_VISIT_COUNT: 13,
    NEW_DASH_FEEDBACK_SENT: 14,
    NEW_DASH_CANCEL_COUNT: 15,
    NEW_VERSION_INFO_SHOWN: 16,
    ENHANCE_HINT_SHOWN: 17,
    PAGE_SIZE_UNIT: 18,
    PRESENTATION_TOUR_SHOWN: 19,
    NARRATION_TOUR_SHOWN: 20,
  }

  function isDefaultTemplateAndProjectEditor(): boolean {
    return user?.value?.permissions?.['superuser'] === 1 && user?.value?.permissions?.['templates.defaults'] === 1
  }

  async function fetchCompanyUsers(): Promise<void> {
    companyUsers.value = await http.get<TUser[]>('/users')
  }

  async function resetPassword(email: string, resetCode: string, password: string): Promise<string> {
    const loader = useLoaderStore()
    const popup = usePopupStore()
    loader.show('Please wait!')
    try {
      const response = await http.post<any>('/users/reset', {
        email,
        resetCode,
        password,
      })
      loader.hide()
      if (response.status === 'error') {
        return response.message
      }
      await popup.alert('Password changed successfully!')
      logout()
      return 'OK'
    } catch {
      loader.hide()
      return 'Error resetting password '
    }
  }

  async function resetPasswordAgencyClient(
    email: string,
    agency: string,
    resetCode: string,
    password: string,
  ): Promise<string> {
    const loader = useLoaderStore()
    const popup = usePopupStore()
    loader.show('Please wait!')
    try {
      const response = await http.post<any>('/clients/reset', {
        email,
        resetCode,
        password,
        agency,
      })
      loader.hide()
      if (response?.status === 'error') {
        return response.error
      }
      await popup.alert('Your password has been set, now you can login')
      logout()
      return 'OK'
    } catch (e) {
      loader.hide()
      console.log(e)
      return 'Error resetting password '
    }
  }

  async function sendEbookEmail(email: string, subject: string, message: string): Promise<boolean> {
    try {
      const result = await http.post(`/users/send_ebook_email`, { email, subject, message }, true)
      return result === 'Email sent'
    } catch (e: any) {
      console.log('Error sending email')
      console.log(e)
      return false
    }
  }

  async function sendResetEmail(email: string, agencySlug: string): Promise<string> {
    const loader = useLoaderStore()
    const popup = usePopupStore()
    loader.show('Please wait!')
    const data = {
      email: email,
      agency: agencySlug,
    }

    const emailMaybeSend =
      'If the email you entered is valid then a message will be sent to your email address with instructions on how to reset your password'
    try {
      await http.post(`/${agencySlug ? 'clients' : 'users'}/send_reset_code`, data, true)
      loader.hide()
      await popup.alert(emailMaybeSend)
      window.location.href = '/dashboard/login'
      return 'OK'
    } catch (e: any) {
      const errors = e?.message ?? ''
      //await http.post('/logs/log_reset_password_failed', { stacktrace: errors }, true)
      loader.hide()
      if (errors.indexOf('No user with email:') !== -1) {
        await popup.alert(emailMaybeSend)
        window.location.href = '/dashboard/login'
      } else if (errors.indexOf('https://go.designrr.io/designrr-activating-account') !== -1) {
        return errors
      } else if (errors.indexOf(' is deactivated!') !== -1) {
        return errors
      } else {
        return 'Error processing request'
      }
      return ''
    }
  }

  async function loginClient(email: string, password: string, agencySlug: string) {
    const loader = useLoaderStore()
    loader.show('Please wait!')
    try {
      const response = await http.post<any>('/clients/login', {
        email,
        password,
        agency: agencySlug,
      })
      if (response.status === 'error') {
        return response.message
      }
      const client = response?.client
      await afterClientLogin(client)
      return 'OK'
    } catch (e: any) {
      return e?.message ?? 'Error'
    } finally {
      loader.hide()
    }
  }

  async function login(email: string, password: string) {
    const loader = useLoaderStore()
    loader.show('Please wait!')
    try {
      const response = await http.post<any>('/users/login', {
        email,
        password,
        device_id: getDeviceId(),
        fingerprint: getFingerprint(),
      })
      if (response.status === 'error') {
        return response.message
      }
      const user = response.user
      const accountType = response.accountType
      const skipIntercom = !!response.skip_intercom
      await afterLogin(user, accountType, skipIntercom)
      return 'OK'
    } catch (e: any) {
      return e?.message ?? 'Error'
    } finally {
      loader.hide()
    }
  }

  function redirectAfterLogin(user: TUser): void {
    const questionnaire = user?.settings?.onboarding_questionaire

    if (!user?.settings || !questionnaire || questionnaire == '{}') {
      window.location.href = '/dashboard/questionnaire'
    } else {
      window.location.href = '/dashboard/'
    }
  }

  async function commonAfterLogin() {
    await flipbooks.clearTmpPdfFiles()
  }

  const stripeAccountStatus = ref<TStripeAccountStatus | null>(
    useStorage('stripeAccountStatus', {} as TStorageStripeAccountStatus).value?.value ?? null,
  )

  // Figures out which Stripe account (current/legacy) this user is on and whether direct
  // in-app charges are enabled for them, ahead of any upgrade/purchase click. Fired from
  // App.vue on dashboard boot (fire-and-forget) - NOT from afterLogin, which is immediately
  // followed by a full-page redirect that would cancel the in-flight request - and awaited
  // by the upgrade modal so it always decides on a fresh answer.
  async function checkStripeAccountStatus(): Promise<TStripeAccountStatus | null> {
    try {
      const status = await http.get<TStripeAccountStatus>('/stripe/account_status')
      const storage = useStorage('stripeAccountStatus', {} as TStorageStripeAccountStatus)
      storage.value.value = status
      storage.value.time = Date.now()
      stripeAccountStatus.value = status
      return status
    } catch {
      // Best-effort only - the purchase flow falls back to the ThriveCart checkout
      // whenever this status is missing or stale.
      return null
    }
  }

  // A user's upgrades go through Stripe directly only when they are on the rollout flag AND
  // their Stripe account has been resolved - anything less and the backend would answer
  // not_eligible anyway, so don't even try.
  function canUseDirectPayments(): boolean {
    const status = stripeAccountStatus.value
    return !!status && status.directPaymentsEnabled && status.stripeAccount !== null
  }

  // Moves the user's existing Stripe subscription to the given plan. On 'ok' the backend
  // returns the fresh user + plan limits (same shape as login), which are stored in place
  // so the UI reflects the new plan without a re-login.
  async function upgradeSubscriptionViaStripe(
    targetAccountTypeId: number,
    source?: string,
  ): Promise<TStripeUpgradeResult> {
    const result = await http.post<TStripeUpgradeResult>('/stripe/upgrade_subscription', {
      targetAccountTypeId,
      source: source ?? null,
    })
    if (result.status === 'ok' && result.user && result.accountType) {
      storeAccount(result.user, result.accountType)
    }
    return result
  }

  function storeAccount(freshUser: TUser, freshAccountType: TAccountType) {
    const storage = useStorage('user', {} as TStorageUser)
    storage.value.value = freshUser
    storage.value.time = Date.now()
    const accountStorage = useStorage('accountType', {} as TStorageAccountType)
    accountStorage.value.value = freshAccountType
    accountStorage.value.time = Date.now()
    user.value = freshUser
    accountType.value = freshAccountType
  }

  async function refreshAccountType(): Promise<void> {
    try {
      const result = await http.post<{ user: TUser; accountType: TAccountType }>('/users/getAccountType')
      if (result?.user?.id && result.user.id === user.value?.id && result.accountType) {
        storeAccount({ ...user.value, ...result.user }, result.accountType)
      }
    } catch (e: any) {
      if (!isAbortError(e)) {
        window?.Bugsnag?.notify(e)
      }
    }
  }

  async function afterLogin(user: TUser, accountType: TAccountType, skipIntercom: boolean) {
    await TimerUtils.wait(550)
    await commonAfterLogin()
    const storage = useStorage('user', {} as TStorageUser)
    const previousUserId = storage.value?.value?.id
    if (previousUserId && previousUserId !== user.id) {
      await clearIDB().catch(() => {})
    }
    storage.value.value = user
    storage.value.time = Date.now()
    const accountStorage = useStorage('accountType', {} as TStorageAccountType)
    accountStorage.value.value = accountType
    accountStorage.value.time = Date.now()
    localStorage.setItem('skipIntercom', skipIntercom.toString())
    IntercomDes.setupIntercom(skipIntercom)
    MixpanelDes.setupMixpanel(skipIntercom)
    BugSnagDes.instance.setBugSnag(user)

    if (mobileCheck() && isProduction()) {
      router.push('/mobile-support')
      return
    }

    redirectAfterLogin(user)
  }

  async function afterClientLogin(client: TClient) {
    const accountType = { agencyClient: true }
    await TimerUtils.wait(550)

    localStorage.setItem(
      'user',
      JSON.stringify({
        value: client,
        time: new Date(),
      }),
    )

    localStorage.setItem(
      'accountType',
      JSON.stringify({
        value: accountType,
        time: new Date(),
      }),
    )

    localStorage.setItem('skipIntercom', 'true')
    IntercomDes.setupIntercom(true)
    MixpanelDes.setupMixpanel(true)
    BugSnagDes.instance.setBugSnag(client)

    if (mobileCheck() && isProduction()) {
      router.push('/mobile-support')
      return
    }

    window.location.href = '/dashboard/client'
  }

  async function getExtraSettings() {
    try {
      extraSettings.value = await http.post<TUserExtraSettings[]>('/extra_settings/getAllExtraSettings')
    } catch (e: any) {
      if (isAbortError(e)) {
        console.log('Request aborted - user is being redirected to login')
        return
      }
      window?.Bugsnag?.notify(e)
      throw new Error("Can't get extra settings", { cause: e })
    }
  }

  async function getDescription(): Promise<void> {
    try {
      description.value = await http.get<TUserDescription>('/users/get_description')
    } catch {
      description.value = {
        description: '',
        account_slug: '',
      }
    }
  }

  async function updateDescription(newDescription: string): Promise<void> {
    const loader = useLoaderStore()
    loader.show('Please wait!')
    try {
      await http.post<TUserDescription>('/users/update_description', { description: newDescription })
      await getDescription()
      loader.hide()
    } catch (e) {
      loader.hide()
      window?.Bugsnag?.notify(e)
      throw new Error("Can't update user description", { cause: e })
    }
  }

  async function getPublicConfig() {
    try {
      publicConfig.value = await http.get<TPublicConfig>('/extra_settings/public_config')
    } catch (e: any) {
      if (isAbortError(e)) {
        console.log('Request aborted - user is being redirected to login')
        return
      }
      window?.Bugsnag?.notify(e)
      throw new Error("Can't get public config", { cause: e })
    }
  }

  async function saveExtraSetting(key: number | string, value: string) {
    let data: TUserExtraSetting
    if (isNumeric(key)) {
      data = {
        value,
        setting_key_id: key.toString(),
        setting_key: '',
      }
    } else {
      data = {
        value,
        setting_key: key.toString(),
      }
    }

    const res = await http.post('/extra_settings/saveExtraSetting', data)
    if (!res) {
      snack.add('Error saving setting!', { type: 'danger' })
    } else {
      await getExtraSettings()
    }
  }

  async function ping() {
    await http.get('/ping', {}, true)
  }

  async function getExtraSettingByKey(key: number): Promise<string> {
    if (typeof extraSettings.value === 'undefined') {
      await new Promise((resolve) => {
        const check = (): any => (typeof extraSettings.value !== 'undefined' ? resolve(true) : setTimeout(check, 100))
        check()
      })
      return await getExtraSettingByKey(key)
    } else {
      const found = extraSettings.value?.find((setting) => setting?.keys?.id === key)
      return Promise.resolve(found?.value || '')
    }
  }

  async function editUserInfo(email: string, user: TUser): Promise<void> {
    if (user.email === '') {
      snack.add('Please enter your email!', { type: 'danger' })
      return
    }
    if (!Validators.validateEmail(user.email)) {
      snack.add('Please check the email, it is not valid!', { type: 'danger' })
      return
    }
    const loader = useLoaderStore()
    const errMsg = 'Error saving the changes! Please try again!'
    try {
      loader.show('Please wait!')
      const res: string = await http.post(
        '/users/update_profile',
        {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
        true,
      )
      loader.hide()
      snack.add(res)
      const storage = useStorage('user', {} as TStorageUser)
      storage.value.value = user
      storage.value.time = Date.now()

      if (email !== user.email) {
        logout()
      }
    } catch {
      loader.hide()
      snack.add(errMsg, { type: 'danger', permanent: true })
      return
    }
  }

  async function changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    const popup = usePopupStore()
    if (newPassword !== confirmPassword) {
      await popup.alert('Passwords do not match!')
      return
    }
    if (!newPassword || newPassword.length < 6) {
      await popup.alert('Password length is too short!')
      return
    }
    if (oldPassword === newPassword) {
      await popup.alert('Old and new password are the same!')
      return
    }
    const loader = useLoaderStore()
    const defaultErrorMsg = 'Error changing the password! Please try again!'
    try {
      loader.show('Please wait!')
      const response = await http.post(
        '/users/change_password',
        {
          old_password: oldPassword,
          new_password: newPassword,
        },
        true,
      )
      loader.hide()
      await popup.alert(response)
      logout()
    } catch (e: any) {
      loader.hide()
      await popup.alert(e?.message ?? defaultErrorMsg)
      return
    }
  }

  async function cancelSubscription(sub: TSubscriptionInfo) {
    return await http.post<{ result: boolean }>('/chatbot/cancel_subscription', {
      payment: sub,
    })
  }

  async function checkMethod(payment: TProductInfo | TSubscriptionInfo) {
    const response = await http.post<{ chatbot?: boolean; reason?: string }>('/chatbot/check_method', {
      payment,
    })
    return response
  }

  async function getBillingHistory(bustCache = false): Promise<TBillingInfo> {
    if (bustCache) {
      billingHistoryCache.value = null
      billingHistoryPromise = null
    }

    if (billingHistoryCache.value) {
      return Promise.resolve(billingHistoryCache.value)
    }

    if (billingHistoryPromise) {
      return billingHistoryPromise
    }

    billingHistoryPromise = (async () => {
      try {
        const response = await fetch('/customer/purchase_history')
        if (!response.ok) {
          throw new Error(`Failed to fetch purchase history. Status: ${response.status}`)
        }
        const billingInfo = await response.json()
        billingHistoryCache.value = billingInfo
        return billingInfo
      } finally {
        billingHistoryPromise = null
      }
    })()
    return billingHistoryPromise
  }

  async function getBillingHistoryFromLogs(bustCache = false): Promise<TBillingInfo> {
    if (bustCache) {
      billingHistoryFromLogsCache.value = null
      billingHistoryFromLogsPromise = null
    }

    if (billingHistoryFromLogsCache.value) {
      return Promise.resolve(billingHistoryFromLogsCache.value)
    }

    if (billingHistoryFromLogsPromise) {
      return billingHistoryFromLogsPromise
    }

    billingHistoryFromLogsPromise = (async () => {
      try {
        const response = await fetch('/api/purchase-history/from-logs')
        if (!response.ok) {
          throw new Error(`Failed to fetch purchase history from logs. Status: ${response.status}`)
        }
        const billingInfo = await response.json()
        billingHistoryFromLogsCache.value = billingInfo
        return billingInfo
      } catch (error) {
        console.error('Failed to fetch billing history from logs:', error)
        // Fallback to empty data rather than crashing
        const fallbackData = {
          subscriptions: [],
          products: [],
        }
        billingHistoryFromLogsCache.value = fallbackData
        return fallbackData
      } finally {
        billingHistoryFromLogsPromise = null
      }
    })()
    return billingHistoryFromLogsPromise
  }

  function getUserFullName(user: TUser | TClient): string {
    return `${user.first_name ? user.first_name : ''} ${user.last_name ? user.last_name : ''}`.trim()
  }

  function getIdStandard(): number[] {
    return [Products.Standard]
  }

  function getIdPro(): number[] {
    return [Products.Pro]
  }

  function getIdPremium(): number[] {
    return [Products.Premium, Products.PremiumForAgency]
  }

  function getIdBusiness(): number[] {
    return [Products.Business]
  }

  function getIdAgencyPremium(): number[] {
    return [Products.AgencyPremium]
  }

  function isClientAccount(): boolean {
    // eslint-disable-next-line
    return user.value.hasOwnProperty('agency_id')
  }

  function isProOrHigherAccount(): boolean {
    if (!user.value?.account_type) {
      return false
    }

    return user.value.account_type > 1
  }

  function isAgencyAccount(): boolean {
    if (!user.value?.account_type) {
      return false
    }

    return [Products.Agency, Products.AgencyPremium, Products.PremiumForAgency].includes(user.value.account_type)
  }

  function canCreatePresentations(): boolean {
    return canCreatePresentationsFor(user.value, accountType.value)
  }

  function hasPremiumPlan(): boolean {
    if (!user.value?.account_type) {
      return false
    }

    return getIdPremium().includes(user.value.account_type)
  }

  function hasPremiumOrHigherPlan(): boolean {
    if (!user.value?.account_type) {
      return false
    }

    return [...getIdPremium(), ...getIdAgencyPremium()].includes(user.value.account_type)
  }

  async function getUserById(userId: number): Promise<TUser | null> {
    const authorUser: { user: TUser } = await http.get('/get_user_by_id/' + userId)
    return authorUser?.user ?? null
  }

  function getUserFullNameOrEmail(): string {
    if (!user.value?.id) {
      return ''
    }

    const fullName = getUserFullName(user.value).trim()
    if (fullName !== '') {
      return fullName
    }

    return user.value.email
  }

  return {
    user,
    companyUsers,
    performLogout,
    logout,
    login,
    loginClient,
    sendResetEmail,
    isAgencyAccount,
    hasPremiumPlan,
    hasPremiumOrHigherPlan,
    isProOrHigherAccount,
    canCreatePresentations,
    refreshAccountType,
    isClientAccount,
    isDefaultTemplateAndProjectEditor,
    resetPassword,
    resetPasswordAgencyClient,
    afterLogin,
    accountType,
    description,
    getDescription,
    updateDescription,
    getIdStandard,
    getIdPro,
    getIdPremium,
    getIdBusiness,
    getIdAgencyPremium,
    getUserById,
    extraSettings,
    publicConfig,
    getExtraSettings,
    getPublicConfig,
    stripeAccountStatus,
    checkStripeAccountStatus,
    canUseDirectPayments,
    upgradeSubscriptionViaStripe,
    saveExtraSetting,
    ping,
    getExtraSettingByKey,
    userSettingLabels,
    editUserInfo,
    changePassword,
    getBillingHistory,
    getBillingHistoryFromLogs,
    fetchCompanyUsers,
    getUserFullName,
    getUserFullNameOrEmail,
    checkMethod,
    sendEbookEmail,
    cancelSubscription,
  }
})
